import React, { useState, useRef } from "react";
import { Upload, FileSpreadsheet, FileText, CheckCircle, XCircle } from "lucide-react";
import { authApiService } from "../../apis/authService";
import toast from "react-hot-toast";

export function MedicationUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "application/vnd.ms-excel.sheet.macroEnabled.12",
        "text/csv",
        "application/csv",
        "text/plain",
      ];
      const allowedExtensions = [".xlsx", ".xls", ".xlsm", ".csv"];
      const fileExtension = selectedFile.name
        .toLowerCase()
        .substring(selectedFile.name.lastIndexOf("."));

      if (
        !allowedTypes.includes(selectedFile.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        toast.error(
          "Invalid file type. Please upload an Excel (.xlsx, .xls, .xlsm) or CSV (.csv) file."
        );
        return;
      }

      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("File size too large. Maximum size is 10MB.");
        return;
      }

      setFile(selectedFile);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("excelFile", file);
      const response = await authApiService.post(
        "/precharting/medications/import-excel",
        formData,
        {
          headers: {
            "Content-Type": undefined,
          },
        }
      );

      const result = response.data?.body || response.data;

      if (result.success) {
        setUploadResult({
          success: true,
          summary: result.data?.summary || {},
          added: result.data?.added || [],
          skipped: result.data?.skipped || [],
          errors: result.data?.errors || [],
          skippedCount: result.data?.skippedCount || 0,
        });
        toast.success(
          result.message || "Medications imported into master successfully"
        );

        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        setUploadResult({
          success: false,
          message: result.message || "Upload failed",
        });
        toast.error(result.message || "Failed to import medications");
      }
    } catch (error) {
      console.error("Medication upload error:", error);
      const errorMessage =
        error.response?.data?.body?.message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to upload file";
      setUploadResult({
        success: false,
        message: errorMessage,
      });
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getFileIcon = () => {
    if (!file) return null;
    const extension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));
    if ([".xlsx", ".xls", ".xlsm"].includes(extension)) {
      return <FileSpreadsheet className="h-8 w-8 text-teal-600" />;
    }
    return <FileText className="h-8 w-8 text-blue-600" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-gray-300 mt-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-blue-600" />
          Medication Master Import
        </h2>
        <p className="text-gray-600">
          Upload Excel (.xlsx, .xls, .xlsm) or CSV (.csv) files to bulk import
          medications into the medication master table.
          <br />
          Only new medications that don't already exist (by name) will be added.
        </p>
      </div>

      {/* File Upload Section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select File
        </label>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.xlsm,.csv"
              onChange={handleFileSelect}
              className="hidden"
              id="medication-file-upload"
            />
            <label
              htmlFor="medication-file-upload"
              className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-400 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Upload className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-700">
                {file
                  ? file.name
                  : "Click to select Excel or CSV file for medications"}
              </span>
            </label>
          </div>
        </div>

        {file && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg flex items-center gap-4">
            {getFileIcon()}
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{file.name}</p>
              <p className="text-xs text-gray-500">
                {formatFileSize(file.size)}
              </p>
            </div>
            <button
              onClick={handleClear}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
            >
              Remove
            </button>
          </div>
        )}
      </div>

      {/* Upload Button */}
      <div className="mb-6">
        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-lg hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-all"
        >
          {uploading ? (
            <>
              <Upload className="h-5 w-5 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-5 w-5" />
              Upload and Import Medications
            </>
          )}
        </button>
      </div>

      {/* File Format Instructions */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200 overflow-hidden">
        <h3 className="text-sm font-semibold text-blue-900 mb-3">
          File Format Requirements:
        </h3>
        <ul className="text-xs sm:text-sm text-blue-800 space-y-3 break-words">
          <li className="leading-relaxed">
            • <strong>Excel/CSV headers (recommended):</strong>
            <div className="flex flex-wrap gap-2 mt-2">
              <code className="bg-white px-2 py-1 rounded border text-xs sm:text-xs break-all">
                medication_name
              </code>
              <code className="bg-white px-2 py-1 rounded border text-xs sm:text-xs break-all">
                formulation
              </code>
              <code className="bg-white px-2 py-1 rounded border text-xs sm:text-xs break-all">
                dosage_per_administration
              </code>
            </div>
          </li>
          <li className="leading-relaxed break-words">
            • If headers differ, the system will try to auto-detect columns
            based on names containing{" "}
            <span className="font-medium">"medication"</span>,{" "}
            <span className="font-medium">"formulation"</span>, and{" "}
            <span className="font-medium">"dosage"</span>.
          </li>
          <li className="leading-relaxed">• First row should be headers.</li>
          <li className="leading-relaxed">• Maximum file size: 10MB.</li>
          <li className="leading-relaxed">
            • Supported formats: .xlsx, .xls, .xlsm, .csv.
          </li>
          <li className="leading-relaxed break-words">
            • Existing medications (same name) will be skipped.
          </li>
        </ul>
      </div>

      {/* Upload Results */}
      {uploadResult && (
        <div
          className={`rounded-lg p-6 border-2 ${
            uploadResult.success
              ? "bg-teal-50 border-teal-300"
              : "bg-red-50 border-red-300"
          }`}
        >
          {uploadResult.success ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="h-6 w-6 text-teal-600" />
                <h3 className="text-lg font-semibold text-teal-900">
                  Medication Upload Successful!
                </h3>
              </div>

              {uploadResult.summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white rounded p-3 text-center">
                    <p className="text-xs text-gray-600">Total in File</p>
                    <p className="text-lg font-bold text-gray-900">
                      {uploadResult.summary.totalInFile || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 text-center">
                    <p className="text-xs text-gray-600">Added</p>
                    <p className="text-lg font-bold text-teal-600">
                      {uploadResult.summary.added || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 text-center">
                    <p className="text-xs text-gray-600">Skipped</p>
                    <p className="text-lg font-bold text-yellow-600">
                      {uploadResult.summary.skipped || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 text-center">
                    <p className="text-xs text-gray-600">Errors</p>
                    <p className="text-lg font-bold text-red-600">
                      {uploadResult.summary.errors || 0}
                    </p>
                  </div>
                </div>
              )}

              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-red-900 mb-2">
                    Errors:
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-white rounded p-3">
                    {uploadResult.errors.slice(0, 10).map((error, index) => (
                      <div key={index} className="text-xs text-red-700 mb-1">
                        Row {error.rowNumber}: {error.medication_name} -{" "}
                        {error.error || error.reason}
                      </div>
                    ))}
                    {uploadResult.errors.length > 10 && (
                      <p className="text-xs text-gray-500 mt-2">
                        ... and {uploadResult.errors.length - 10} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2">
              <XCircle className="h-6 w-6 text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-red-900">
                  Medication Upload Failed
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  {uploadResult.message}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
